import { useTranslation } from 'react-i18next';
import { Repeat } from 'lucide-react';
import { RecurrencePattern, IssueStatus } from '@momentum/types';
import React, { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Label } from '@/shared/components/ui/Label';
import { Input } from '@/shared/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/RadioGroup';

// Default values for recurrence patterns
const DEFAULT_INTERVAL_DAYS = 7;

interface RecurrenceConfigFieldProps {
  form: any;
  fieldPath: string; // e.g., "recurrence" or "keyResults.0.recurrence"
  label: string; // e.g., "repeatableTask" or "repeatableKeyResult"
  infoText?: string; // Optional additional info text
  disabled?: boolean;
}

export const RecurrenceConfigField: React.FC<RecurrenceConfigFieldProps> = ({
  form,
  fieldPath,
  label,
  infoText,
  disabled,
}) => {
  const { t } = useTranslation('goals');

  // Initialize isRepeatable from form value
  const isInitialRecurrenceConfigExists = !!form.getFieldValue(
    `${fieldPath}.config`
  );
  const [isRepeatable, setIsRepeatable] = useState(
    isInitialRecurrenceConfigExists
  );

  // Track current pattern to initialize pattern-specific fields
  const [currentPattern, setCurrentPattern] = useState<
    RecurrencePattern | undefined
  >(
    isInitialRecurrenceConfigExists
      ? form.getFieldValue(`${fieldPath}.config.pattern`)
      : undefined
  );

  // Track end config type
  const [endConfigType, setEndConfigType] = useState<
    'none' | 'after_occurrences' | 'until_date' | undefined
  >(
    isInitialRecurrenceConfigExists
      ? form.getFieldValue(`${fieldPath}.endConfig`)?.type || 'none'
      : undefined
  );

  // Use ref to track if change is user-initiated to prevent circular updates
  const isUserInitiatedRef = useRef(false);

  // Sync state when form value changes externally
  useEffect(() => {
    if (isUserInitiatedRef.current) {
      isUserInitiatedRef.current = false;
      return;
    }

    const isCurrentRecurrenceConfigExists = !!form.getFieldValue(
      `${fieldPath}.config`
    );
    if (isCurrentRecurrenceConfigExists !== isRepeatable) {
      setIsRepeatable(isCurrentRecurrenceConfigExists);
    }
  }, [fieldPath, form, isRepeatable]);

  // Track pattern changes for conditional rendering
  useEffect(() => {
    if (!isRepeatable) return;

    const pattern = form.getFieldValue(`${fieldPath}.config.pattern`);

    if (pattern !== currentPattern) {
      setCurrentPattern(pattern);
    }
  }, [isRepeatable, currentPattern, fieldPath, form]);

  // Sync endConfig type when recurrence is toggled off
  useEffect(() => {
    if (!isRepeatable) {
      setEndConfigType(undefined);
    }
  }, [isRepeatable]);

  return (
    <div className="space-y-4">
      {/* Recurrence Toggle */}
      <div className="flex items-center gap-2">
        <Checkbox
          className="border-foreground"
          checked={isRepeatable}
          disabled={disabled}
          onCheckedChange={(checked) => {
            const isChecked = checked === true;
            isUserInitiatedRef.current = true;
            setIsRepeatable(isChecked);
            if (isChecked) {
              // Initialize recurrence config when enabled
              const currentRecurrence = form.getFieldValue(fieldPath);
              if (!currentRecurrence || !currentRecurrence.config) {
                form.setFieldValue(fieldPath, {
                  config: {
                    pattern: RecurrencePattern.DAILY,
                  },
                  startDate: new Date().toISOString().split('T')[0],
                });
                setEndConfigType('none');
              } else {
                // Ensure endConfig type is set
                const existingEndConfigType = currentRecurrence.endConfig?.type;
                setEndConfigType(existingEndConfigType || 'none');
              }

              // For task recurrence (fieldPath === "recurrence"), update status and startDate
              if (fieldPath === 'recurrence') {
                // Set status to TODO when recurrence is enabled
                form.setFieldValue('status', IssueStatus.TODO);
                // Enforce hard invariant: clear startDate when recurrence exists
                form.setFieldValue('startDate', undefined);
              }
            } else {
              // Clear recurrence when disabled
              form.setFieldValue(fieldPath, undefined);
              setEndConfigType(undefined);
            }
          }}
        />
        <Label className="text-sm font-medium flex items-center gap-2 flex-1">
          <Repeat className="h-4 w-4" />
          {t(label as any)}
        </Label>
      </div>

      {/* Recurrence Configuration */}
      {isRepeatable && (
        <div className="space-y-4 ps-6">
          {/* Pattern Selector */}
          <form.AppField
            name={`${fieldPath}.config.pattern`}
            children={(field: any) => (
              <div className="space-y-2">
                <Label>{t('recurrencePattern')}</Label>
                <Select
                  value={field.state.value || RecurrencePattern.DAILY}
                  onValueChange={(value: RecurrencePattern) => {
                    field.handleChange(value);
                    setCurrentPattern(value);

                    // Initialize pattern-specific fields with defaults
                    if (value === RecurrencePattern.CUSTOM_DAYS) {
                      const currentIntervalDays = form.getFieldValue(
                        `${fieldPath}.config.intervalDays`
                      );
                      if (!currentIntervalDays) {
                        form.setFieldValue(
                          `${fieldPath}.config.intervalDays`,
                          DEFAULT_INTERVAL_DAYS
                        );
                      }
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RecurrencePattern.DAILY}>
                      {t('recurrenceDaily')}
                    </SelectItem>
                    <SelectItem value={RecurrencePattern.WEEKLY}>
                      {t('recurrenceWeekly')}
                    </SelectItem>
                    <SelectItem value={RecurrencePattern.CUSTOM_DAYS}>
                      {t('recurrenceCustom')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          {/* Pattern-Specific Fields */}
          {currentPattern === RecurrencePattern.CUSTOM_DAYS && (
            <form.AppField
              name={`${fieldPath}.config.intervalDays`}
              children={(field: any) => (
                <div className="space-y-2">
                  <Label>{t('intervalDays')}</Label>
                  <Input
                    type="number"
                    value={field.state.value || ''}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    disabled={disabled}
                    min={1}
                    placeholder={String(DEFAULT_INTERVAL_DAYS)}
                  />
                </div>
              )}
            />
          )}

          {/* Recurrence Start Date */}
          <form.AppField
            name={`${fieldPath}.startDate`}
            children={(field: any) => (
              <div className="space-y-2">
                <Label>{t('recurrenceStartDate')}</Label>
                <Input
                  type="date"
                  value={field.state.value?.slice(0, 10) || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={disabled}
                  min={new Date().toISOString().slice(0, 10)}
                  required
                />
              </div>
            )}
          />

          {/* Recurrence End Configuration */}
          <div className="space-y-3">
            <Label>{t('recurrenceEnd')}</Label>
            <RadioGroup
              value={endConfigType || 'none'}
              onValueChange={(
                value: 'none' | 'after_occurrences' | 'until_date'
              ) => {
                setEndConfigType(value);
                if (value === 'none') {
                  form.setFieldValue(`${fieldPath}.endConfig`, undefined);
                } else if (value === 'after_occurrences') {
                  const currentCount = form.getFieldValue(
                    `${fieldPath}.endConfig.count`
                  );
                  form.setFieldValue(`${fieldPath}.endConfig`, {
                    type: 'after_occurrences',
                    count: currentCount || 10,
                  });
                } else {
                  const currentDate = form.getFieldValue(
                    `${fieldPath}.endConfig.date`
                  );
                  const startDate = form.getFieldValue(
                    `${fieldPath}.startDate`
                  );
                  form.setFieldValue(`${fieldPath}.endConfig`, {
                    type: 'until_date',
                    date: currentDate || startDate || '',
                  });
                }
              }}
              disabled={disabled}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="none" id="none" />
                <Label htmlFor="none" className="font-normal">
                  {t('noEndDate')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="after_occurrences"
                  id="after_occurrences"
                />
                <Label htmlFor="after_occurrences" className="font-normal">
                  {t('endAfterOccurrences')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="until_date" id="until_date" />
                <Label htmlFor="until_date" className="font-normal">
                  {t('repeatUntilDate')}
                </Label>
              </div>
            </RadioGroup>

            {/* End Config Fields */}
            {endConfigType === 'after_occurrences' && (
              <form.AppField
                name={`${fieldPath}.endConfig.count`}
                children={(field: any) => (
                  <div className="space-y-2 ps-6">
                    <Label>{t('numberOfOccurrences')}</Label>
                    <Input
                      type="number"
                      value={field.state.value || 10}
                      onChange={(e) => {
                        const count = Number(e.target.value);
                        field.handleChange(count);
                        form.setFieldValue(`${fieldPath}.endConfig`, {
                          type: 'after_occurrences',
                          count,
                        });
                      }}
                      disabled={disabled}
                      min={1}
                      required
                    />
                  </div>
                )}
              />
            )}

            {endConfigType === 'until_date' && (
              <form.AppField
                name={`${fieldPath}.endConfig.date`}
                children={(field: any) => (
                  <div className="space-y-2 ps-6">
                    <Label>{t('endDate')}</Label>
                    <Input
                      type="date"
                      value={field.state.value || ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        field.handleChange(date);
                        form.setFieldValue(`${fieldPath}.endConfig`, {
                          type: 'until_date',
                          date,
                        });
                      }}
                      disabled={disabled}
                      min={new Date().toISOString().slice(0, 10)}
                      required
                    />
                  </div>
                )}
              />
            )}
          </div>

          {/* Informational Text */}
          {infoText && (
            <div className="text-xs text-muted-foreground">
              {t(infoText as any)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
